import { capitalize } from '@mui/material';
import dayjs from 'dayjs';

export const ANNUAL_EXPERT_PRICES = { CZK: 369000, EUR: 14800 } as const;
export const annualExpertPriceLabel = (currency: 'CZK' | 'EUR') =>
  currency === 'EUR' ? '148 €' : '3 690 Kč';

export type BillingPeriod = 'MONTHLY' | 'YEARLY';

export type SubscriptionPlans = 'pro' | 'expert';

export enum SubscriptionPlansEnum {
  PRO = 'pro',
  EXPERT = 'expert',
}

export type ImportStatus = 'PAID' | 'CANCELLED' | 'PENDING';
export type SubscriptionStates = 'ACTIVE' | 'CANCELLED' | 'EXPIRED' | 'PENDING';

export type SubscriptionCreateData = {
  billingPeriod?: BillingPeriod;
  plan: SubscriptionPlans;
  status: SubscriptionStates;
  currency: 'CZK' | 'EUR';
  price: number;
};

export type Subscription = {
  billingPeriod?: BillingPeriod;
  autoRenew?: boolean;
  id: string;
  plan: SubscriptionPlans;
  status: SubscriptionStates;
  currency: 'CZK' | 'EUR';
  price: number;
  endDate: string;
  startDate: string;
  userId: string;
};
export type CreateImportPayment = {
  currency: 'CZK' | 'EUR';
  price: number;
};

export type Payment = {
  id: string;
  refId: string;
  status: ImportStatus;
};

export type SubscriptionChangeDialogProps = {
  isTrial?: boolean;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentPlan: SubscriptionPlans;
  newPlan: SubscriptionPlans;
  endDate: string;
  currency?: 'CZK' | 'EUR';
};

export const SUBSCRIPTION_CONFIG = {
  pro: {
    price: 239,
    dailyRate: 239 / 30,
    priceEur: 9.5,
    dailyRateEur: 9.5 / 30,
    name: 'PRO',
    features: [
      'Osobní diář návštěv',
      'Správa procedur všech návštěv',
      'Chytré vyskladňování skladu',
      'Automatický nákupní seznam',
      'Hlídací pes zásob',
      'Přehled tržeb a nákladů',
      'Sdílený sklad a vyúčtování',
    ],
  },
  expert: {
    price: 359,
    dailyRate: 359 / 30,
    priceEur: 14.4,
    dailyRateEur: 14.4 / 30,
    name: capitalize(SubscriptionPlansEnum.EXPERT),
    features: [
      'Osobní diář návštěv',
      'Správa procedur všech návštěv',
      'Chytré vyskladňování skladu',
      'Automatický nákupní seznam',
      'Hlídací pes zásob',
      'Přehled tržeb a nákladů',
      'Sdílený sklad a vyúčtování',
      'QR vstupenka = rezervace bez čekání',
      'Cenová kalkulačka = ceny, co vydělávají',
      'Věrnostní program = výhody pro stálé klienty',
      'QR platby = generování QR plateb přímo v aplikaci',
      'Prodej zboží = tržby bez práce',
    ],
  },
} as const;

export const DAYS_PER_MONTH = 30;

export const calculateRemainingDays = (endDate: string): number => {
  const now = dayjs();
  const end = dayjs(endDate);
  return Math.max(0, end.diff(now, 'day'));
};

export const calculateCredit = (dailyRate: number, remainingDays: number): number => {
  return dailyRate * remainingDays;
};

export const calculateBonusDays = (creditInCzk: number, newDailyRate: number): number => {
  return Math.floor(creditInCzk / newDailyRate);
};

export const getFeatureDifference = (
  currentFeatures: readonly string[],
  newFeatures: readonly string[],
): string[] => {
  return newFeatures.filter(f => !currentFeatures.includes(f));
};

export const PRICING_PLANS = {
  pro: {
    title: 'Tarif PRO',
    price: 'Kč 239',
    priceEur: '9,5 €',
    priceValue: 23900,
    priceValueEur: 950,
    period: '/měsíc',
    trialInfo: 'Prvních 14 dní jen za 1 Kč',
    trialInfoEur: 'Prvních 14 dní jen za 1 €',
    features: [
      'Osobní diář návštěv',
      'Správa procedur všech návštěv',
      'Chytré vyskladňování skladu',
      'Automatický nákupní seznam',
      'Hlídací pes zásob',
      'Přehled tržeb a nákladů',
      'Sdílený skladu a vyúčtování',
      'a další...',
    ],
  },
  expert: {
    title: 'Tarif EXPERT',
    price: 'Kč 359',
    priceEur: '14,4 €',
    priceValue: 35900,
    priceValueEur: 1440,
    period: '/měsíc',
    trialInfo: 'Prvních 14 dní jen za 1 Kč',
    trialInfoEur: 'Prvních 14 dní jen za 1 €',
    features: [
      'Vše z tarifu PRO',
      'QR kód pro registraci zákazníků',
      'Zobrazení volných termínů',
      'Rezervace z mobilu i počítače bez hesla',
      'Automatické potvrzení rezervace',
      'Chytrá kalkulačka cen',
      'Věrnostní program pro zákazníky',
      'Automatické SMS připomínky 1,49 Kč / SMS',
    ],
  },
};

export const shouldShowPlan = (
  planType: SubscriptionPlans,
  subscription: Subscription | undefined,
  plan: SubscriptionPlans | null,
  isChangingSubscription: boolean,
  hasDeletedAccountError: boolean,
): boolean => {
  if (hasDeletedAccountError) {
    return false;
  }

  if (
    subscription &&
    (subscription.status === 'EXPIRED' ||
      (!!subscription.endDate && !dayjs(subscription.endDate).isAfter(dayjs())))
  ) {
    return true;
  }

  if (plan === planType) {
    return true;
  }

  if (subscription && subscription.plan === planType) {
    return true;
  }

  if (isChangingSubscription && subscription) {
    if (
      planType === SubscriptionPlansEnum.PRO &&
      subscription.plan === SubscriptionPlansEnum.EXPERT
    ) {
      return true;
    }
    if (
      planType === SubscriptionPlansEnum.EXPERT &&
      subscription.plan === SubscriptionPlansEnum.PRO
    ) {
      return true;
    }
    if (subscription.plan === planType) {
      return false;
    }
  }

  return false;
};
